import React, { useRef, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal, Select, theme, Spinner } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import { useConsentForms, ConsentForm } from '../../../../hooks/useConsentForms'
import { Carer } from '@lmc/ts-common'
import ConsentStatusTag from './ConsentStatusTag'
import { ConsentStatus, STATUS_TRANSLATION_KEYS } from '@lmc/common/types'

interface LinkConsentModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  residentId: string
  linkedConsentIds?: string[]
  onConfirm?: (selectedIds: string[]) => void
}

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD/MM/YYYY - HH:mm')
}

const LinkConsentModal: React.FC<LinkConsentModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  residentId,
  linkedConsentIds = [],
  onConfirm
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const selectedItemsRef = useRef<string[]>(linkedConsentIds)
  const carers: Carer[] = useSelector((state: any) => state.data.carers)

  const { data: consentForms, isLoading } = useConsentForms({
    residentId,
    enabled: isModalVisible && !!residentId
  })

  const getCarerName = (carerId: string): string => {
    const carer = carers?.find((c) => c.id === carerId)
    return carer ? `${carer.name.first} ${carer.name.last}` : ''
  }

  const handleConfirm = () => {
    onConfirm?.(selectedItemsRef.current)
    selectedItemsRef.current = []
    setIsModalVisible(false)
  }

  const handleCancel = () => {
    selectedItemsRef.current = []
  }

  const onSelectChange = (
    options: { label?: React.ReactNode; value: string }[]
  ) => {
    selectedItemsRef.current = options.map((option) => option.value)
  }

  const getDescription = (form: ConsentForm): string => {
    const isSigned = !!form.signedOn && !!form.signedBy
    if (isSigned) {
      return t(carePlansConstants.CONSENT_SIGNED_ON, {
        date: formatDate(form.signedOn!),
        createdBy: getCarerName(form.signedBy!)
      })
    }
    return t(carePlansConstants.CONSENT_CREATED_ON, {
      date: formatDate(form.createdOn),
      createdBy: getCarerName(form.createdBy)
    })
  }

  const selectOptions = useMemo(() => {
    return [...consentForms]
      .sort((a: ConsentForm, b: ConsentForm) => {
        // Sort archived items to the bottom
        if (a.isArchived && !b.isArchived) return 1
        if (!a.isArchived && b.isArchived) return -1
        return 0
      })
      .map((form: ConsentForm) => ({
        key: form.id,
        value: form.id,
        label: form.title,
        form
      }))
  }, [consentForms])

  const getStatusLabel = (status: string): string => {
    const translationKey = STATUS_TRANSLATION_KEYS[status as ConsentStatus]
    return translationKey ? t(translationKey) : status
  }

  const renderOption = (option: any) => {
    const form = option.data.form as ConsentForm
    return (
      <OptionContainer>
        <OptionContent>
          <OptionTitle>{form.title}</OptionTitle>
          <OptionDescription>{getDescription(form)}</OptionDescription>
        </OptionContent>
        {form.isArchived ? (
          <ConsentStatusTag status={ConsentStatus.provided} label="Archived" />
        ) : (
          form.consentStatus && (
            <ConsentStatusTag
              status={form.consentStatus}
              label={getStatusLabel(form.consentStatus)}
            />
          )
        )}
      </OptionContainer>
    )
  }

  return (
    <Modal
      noPadding
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      localisation={{
        title: t(carePlansConstants.CONSENT_MODAL_TITLE),
        confirmText: t(carePlansConstants.CONSENT_MODAL_CONFIRM),
        cancelText: t(carePlansConstants.CONSENT_MODAL_CANCEL)
      }}
    >
      {isLoading ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : (
        <Select<string[], { label?: React.ReactNode; value: string }>
          placeholder={t(carePlansConstants.CONSENT_MODAL_PLACEHOLDER)}
          style={{ width: '100%' }}
          defaultValue={linkedConsentIds}
          mode="multiple"
          onChange={(_value, options) => onSelectChange(options as [])}
          options={selectOptions}
          optionRender={renderOption}
          dropdownStyle={{ maxWidth: '100vw' }}
        />
      )}
    </Modal>
  )
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40px;
`

const OptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 3px 0;
  gap: 8px;
`

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const OptionTitle = styled(Text)`
  font-weight: 500;
  color: ${theme.color.neutral.color2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const OptionDescription = styled(Text)`
  font-size: 12px;
  color: ${theme.color.neutral.color3};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default LinkConsentModal
